import { Fragment } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { scrollToSection } from "@/lib/scrollTo";
import { HeroStats } from "@/components/landing/HeroStats";

const HERO_IMAGE =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_renewable-ag-infra/artifacts/fx64g11z_458c21bf-6782-4d5a-ab32-3bbe400cca8d.png";

const RevealWord = ({ text, delay }) => (
  <span className="inline-block overflow-hidden pb-1 align-bottom">
    <motion.span
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
      className="inline-block"
    >
      {text}
    </motion.span>
  </span>
);

const LINES = [
  ["Reliable", "Energy", "for", "Farms", "—"],
  ["Even", "When", "the", "Grid", "Isn't"],
];

export const Hero = () => {
  const scrollTo = (href) => scrollToSection(href);

  return (
    <section id="top" data-testid="hero-section" className="relative bg-baseLight px-4 sm:px-6 pt-4 sm:pt-6 pb-[30px] sm:pb-[40px] lg:pb-[70px]">
      <div className="relative rounded-[23px] sm:rounded-[31px] overflow-hidden h-[640px] sm:h-[760px] lg:h-[92vh] lg:min-h-[680px] lg:max-h-[920px]">
        <motion.div
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0"
        >
          <img
            src={HERO_IMAGE}
            alt="Aerial view of greenhouse agriculture facility in the Fraser Valley, British Columbia"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-ink/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/35 to-ink/15" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/45 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/10 to-transparent sm:hidden" />

        <div className="absolute bottom-8 sm:bottom-10 lg:bottom-14 left-6 sm:left-10 lg:left-14 right-6 sm:right-10 lg:right-14">
          <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between xl:gap-10">
            <div className="max-w-4xl lg:overflow-visible">
              <h1 data-testid="hero-headline" className="font-heading font-semibold text-4xl sm:text-5xl lg:text-[55px] tracking-tight leading-[1.1] lg:leading-[1.08] lg:overflow-visible text-white">
                {LINES.map((line, li) => (
                  <div key={li}>
                    {line.map((w, i) => (
                      <Fragment key={i}>
                        {li === 0 && i === 2 && <br className="lg:hidden" />}
                        <span>
                          <RevealWord text={w} delay={0.15 + (li * line.length + i) * 0.06} />{" "}
                        </span>
                      </Fragment>
                    ))}
                  </div>
                ))}
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                data-testid="hero-subheadline"
                className="mt-5 text-white sm:text-white/85 text-[15px] sm:text-base leading-relaxed max-w-md lg:max-w-2xl"
              >
                Solar and hydrogen long-duration energy systems designed to reduce diesel
                dependence, store surplus renewable energy, and keep critical farm operations
                running through extended outages.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="mt-7 flex flex-wrap items-center gap-x-7 gap-y-4"
              >
                <motion.button
                  whileHover={{ scale: 1.035 }}
                  whileTap={{ scale: 0.97 }}
                  data-testid="hero-cta-primary"
                  onClick={() => scrollTo("#contact")}
                  className="group relative flex w-full justify-start h-10 items-center overflow-hidden bg-white text-ink text-sm font-medium rounded-full px-6 shadow-lg lg:inline-flex lg:w-auto"
                >
                  <motion.span
                    aria-hidden="true"
                    className="absolute inset-y-0 -left-1/2 w-1/2 bg-gradient-to-r from-transparent via-teal/25 to-transparent skew-x-[20deg]"
                    animate={{ x: ["0%", "420%"] }}
                    transition={{ duration: 2.6, repeat: Infinity, repeatDelay: 1.4, ease: "easeInOut" }}
                  />
                  <span className="relative">Request a Farm Energy Assessment</span>
                </motion.button>

                <Link
                  to="/technology"
                  data-testid="hero-cta-secondary"
                  className="group flex w-full justify-start items-center gap-1.5 text-sm text-white mb-[10px] lg:inline-flex lg:w-auto lg:text-white/80 lg:mb-0 hover:text-white transition-colors duration-300"
                >
                  <span className="underline decoration-white/30 group-hover:decoration-white/70 transition-colors duration-300">
                    Explore the Technology
                  </span>
                  <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                </Link>
              </motion.div>
            </div>
            <HeroStats />
          </div>
        </div>
      </div>
    </section>
  );
};
